/**
 * Bug Reports Panel
 *
 * Admin triage view for in-app bug reports. Lists reports (via trpc.bugs.list),
 * filters by status, shows metadata + screenshot + expandable console logs, and
 * lets an admin change status / add notes (via trpc.bugs.updateStatus).
 * Used inside AdminPanel as a tab.
 */

import { useState, useEffect, useCallback } from 'react';
import { trpc } from '../../lib/trpc';
import { Icon } from '../Icon';
import {
  BUG_REPORT_STATUSES,
  type BugReport,
  type BugReportStatus,
} from '@machina/core';

const GOLD = '#d4af37';

const STATUS_COLORS: Record<BugReportStatus, string> = {
  open: '#e67e22',
  in_progress: '#5b9bd5',
  resolved: '#2ecc71',
  closed: '#95a5a6',
  wontfix: '#e74c3c',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: '#95a5a6',
  medium: '#5b9bd5',
  high: '#e67e22',
  critical: '#e74c3c',
};

type Filter = 'all' | BugReportStatus;

export function BugReportsPanel() {
  const [reports, setReports] = useState<BugReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});

  const loadReports = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await trpc.bugs.list.query(
        filter === 'all' ? undefined : { status: filter },
      );
      setReports(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  async function handleUpdate(
    report: BugReport,
    status: BugReportStatus,
  ) {
    setSavingId(report.id);
    setError(null);
    try {
      await trpc.bugs.updateStatus.mutate({
        id: report.id,
        status,
        adminNotes: notesDraft[report.id] ?? report.adminNotes ?? undefined,
      });
      await loadReports();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div>
      {error && (
        <div style={s.error}>
          <Icon name="alert" size={16} />
          {error}
        </div>
      )}

      {/* Filter bar */}
      <div style={s.filterBar}>
        {(['all', ...BUG_REPORT_STATUSES] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={filter === f ? s.filterActive : s.filterBtn}
          >
            {f === 'all' ? 'All' : f.replace('_', ' ')}
          </button>
        ))}
        <button onClick={loadReports} style={{ ...s.filterBtn, marginLeft: 'auto' }}>
          <Icon name="refresh-cw" size={12} /> Refresh
        </button>
      </div>

      {isLoading ? (
        <div style={s.loading}>
          <Icon name="loader" size={24} />
          Loading bug reports...
        </div>
      ) : reports.length === 0 ? (
        <div style={s.empty}>No bug reports{filter !== 'all' ? ` with status "${filter}"` : ''}.</div>
      ) : (
        <div style={s.list}>
          {reports.map((r) => {
            const isOpen = expanded === r.id;
            return (
              <div key={r.id} style={s.card}>
                <div
                  style={s.cardHeader}
                  onClick={() => setExpanded(isOpen ? null : r.id)}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={s.titleRow}>
                      <span style={s.cardTitle}>{r.title}</span>
                      <span
                        style={{
                          ...s.badge,
                          color: STATUS_COLORS[r.status],
                          borderColor: STATUS_COLORS[r.status],
                        }}
                      >
                        {r.status.replace('_', ' ')}
                      </span>
                      <span
                        style={{
                          ...s.badge,
                          color: PRIORITY_COLORS[r.priority] ?? '#c0c0c0',
                          borderColor: PRIORITY_COLORS[r.priority] ?? '#c0c0c0',
                        }}
                      >
                        {r.priority}
                      </span>
                    </div>
                    <div style={s.meta}>
                      {r.userEmail || 'unknown'} • {r.currentRoute || '—'} •{' '}
                      {new Date(r.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} />
                </div>

                {isOpen && (
                  <div style={s.cardBody}>
                    {r.description && (
                      <div style={s.section}>
                        <div style={s.sectionLabel}>Description</div>
                        <div style={s.descText}>{r.description}</div>
                      </div>
                    )}

                    {r.screenshotUrl && (
                      <div style={s.section}>
                        <div style={s.sectionLabel}>Screenshot</div>
                        <a href={r.screenshotUrl} target="_blank" rel="noreferrer">
                          <img
                            src={r.screenshotUrl}
                            alt="Bug screenshot"
                            style={s.thumb}
                          />
                        </a>
                      </div>
                    )}

                    {r.consoleLogs.length > 0 && (
                      <div style={s.section}>
                        <div style={s.sectionLabel}>
                          Console logs ({r.consoleLogs.length})
                        </div>
                        <div style={s.logs}>
                          {r.consoleLogs.map((log, i) => (
                            <div key={i} style={s.logLine}>
                              <span
                                style={{
                                  color:
                                    log.level === 'error' ? '#e57373' : '#e6b450',
                                  fontWeight: 600,
                                }}
                              >
                                [{log.level}]
                              </span>{' '}
                              {log.message}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div style={s.section}>
                      <div style={s.sectionLabel}>System</div>
                      <div style={s.metaMono}>
                        {r.browserInfo || '—'}
                        {r.screenSize ? ` • ${r.screenSize}` : ''}
                      </div>
                    </div>

                    {/* Admin controls */}
                    <div style={s.section}>
                      <div style={s.sectionLabel}>Admin notes</div>
                      <textarea
                        value={notesDraft[r.id] ?? r.adminNotes ?? ''}
                        placeholder="Triage notes..."
                        onChange={(e) =>
                          setNotesDraft((d) => ({ ...d, [r.id]: e.target.value }))
                        }
                        rows={2}
                        style={s.notes}
                      />
                    </div>

                    <div style={s.actions}>
                      <select
                        value={r.status}
                        onChange={(e) =>
                          handleUpdate(r, e.target.value as BugReportStatus)
                        }
                        disabled={savingId === r.id}
                        style={s.select}
                      >
                        {BUG_REPORT_STATUSES.map((st) => (
                          <option key={st} value={st}>
                            {st.replace('_', ' ')}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleUpdate(r, r.status)}
                        disabled={savingId === r.id}
                        style={s.saveBtn}
                      >
                        {savingId === r.id ? (
                          <Icon name="loader" size={12} />
                        ) : (
                          <Icon name="save" size={12} />
                        )}
                        Save notes
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    marginBottom: 12,
    backgroundColor: 'rgba(220,53,69,0.1)',
    border: '1px solid #c0392b',
    borderRadius: 4,
    color: '#e57373',
  },
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 16,
    alignItems: 'center',
  },
  filterBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    backgroundColor: 'transparent',
    border: '1px solid #3a3242',
    borderRadius: 4,
    color: '#c0c0c0',
    fontSize: 12,
    cursor: 'pointer',
    textTransform: 'capitalize',
  },
  filterActive: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    backgroundColor: `${GOLD}22`,
    border: `1px solid ${GOLD}`,
    borderRadius: 4,
    color: GOLD,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    textTransform: 'capitalize',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 32,
    color: 'var(--color-text-secondary)',
  },
  empty: {
    padding: 24,
    textAlign: 'center',
    color: 'var(--color-text-muted)',
    fontStyle: 'italic',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  card: {
    border: '1px solid #2a2430',
    borderRadius: 6,
    backgroundColor: '#1a1520',
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    cursor: 'pointer',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  cardTitle: {
    fontWeight: 600,
    color: '#e0dcd0',
  },
  meta: {
    fontSize: 11,
    color: '#8a8294',
    marginTop: 4,
  },
  metaMono: {
    fontSize: 11,
    color: '#8a8294',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  badge: {
    display: 'inline-block',
    padding: '1px 7px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 600,
    border: '1px solid',
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
  },
  cardBody: {
    padding: '0 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    borderTop: '1px solid #2a2430',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  sectionLabel: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: GOLD,
    marginTop: 8,
  },
  descText: {
    fontSize: 13,
    color: '#c0c0c0',
    whiteSpace: 'pre-wrap',
  },
  thumb: {
    maxWidth: '100%',
    maxHeight: 240,
    borderRadius: 4,
    border: '1px solid #3a3242',
  },
  logs: {
    maxHeight: 180,
    overflowY: 'auto',
    padding: 8,
    backgroundColor: '#0a080c',
    borderRadius: 4,
    border: '1px solid #2a2430',
    fontFamily: 'monospace',
    fontSize: 11,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  logLine: {
    color: '#c0c0c0',
    wordBreak: 'break-all',
  },
  notes: {
    width: '100%',
    boxSizing: 'border-box',
    padding: 8,
    backgroundColor: '#0a080c',
    border: '1px solid #3a3242',
    borderRadius: 4,
    color: '#e0dcd0',
    fontSize: 13,
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  actions: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  select: {
    padding: '6px 8px',
    backgroundColor: '#0a080c',
    border: '1px solid #3a3242',
    borderRadius: 4,
    color: '#e0dcd0',
    fontSize: 12,
    cursor: 'pointer',
    textTransform: 'capitalize',
  },
  saveBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 12px',
    backgroundColor: 'transparent',
    border: `1px solid ${GOLD}`,
    borderRadius: 4,
    color: GOLD,
    fontSize: 12,
    cursor: 'pointer',
  },
};
